import { getCurrentUserApi } from '@apis/cp/cp-auth.js';
import { AppLoader, NoInternetBanner, NtnRequirementModal, SideMenu, TopHeader } from '@components-web/index.js';
import { useGetRequest, useKeycloak } from '@hooks/index.js';
import { clearUserInfo, setUserInfo } from '@reducers/cp/user-slice/index.js';
import { fetchTenantConfiguration } from '@reducers/tenant-slice/index.js';
import { Flex, Layout } from 'antd';
import { Suspense, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import {
  /*CHAT_PATH,*/ NOT_FOUND as CP_NOT_FOUND,
  SERVER_ERROR as CP_SERVER_ERROR,
  LOGIN_PATH,
} from './constants/cp-paths.js';
import {
  LOGIN_PATH as DUBIZZLE_LOGIN_PATH,
  /*CHAT_PATH,*/ NOT_FOUND as OMS_NOT_FOUND,
  SERVER_ERROR as OMS_SERVER_ERROR,
  PROFILE_SETTINGS,
  RESET_PASSWORD_PATH,
  SIGNUP_PATH,
  STORE_NOT_FOUND,
  TERMS_AND_CONDITIONS,
} from './constants/seller-center-paths.js';
import AppRoutes from './router';
import { getErrorString, handleOMSLogout, sanitizePathname } from './utils/utils.js';
import './App.scss';
import './styles/antd.customize.scss';
import './styles/utils.scss';
import { acceptTermsAndConditionsApi } from '@apis/seller-center/profile-settings.js';
import { IconVacation } from '@common-web/icons/index.jsx';
import { AccountStatusBanner } from '@components-web';
import TaxValidationBanner from '@components-web/tax-validation-banner';
import { DATE_FORMATS } from '@constants/generic-constants.js';
import { ACTION_UPDATE, PERMISSION_KEY_STORE } from '@constants/permission-constants.js';
import { strings } from '@constants/strings.js';
import { setUserInfo as setSellerCenterUserInfo } from '@reducers/seller-center/userinfo-slice/index.js';
import PermissionsHelper from 'auth/helper/PermissionHelper.js';
import dayjs from 'dayjs';
import { useIdleTimer } from 'react-idle-timer';
import { Alert, Button, ButtonSecondary, Modal, Toast } from './common/web-view/index.js';
import ConfigDB from './data/customizer/theme';
import { useIsOffline } from './hooks/index.js';
import DisableVacationModal from './pages/seller-center/accounts-and-settings/profile-settings/vacation-mode/disable-modal/index.jsx';
import { isCP } from './utils/app-utils.js';

const { Content } = Layout;

const App = () => {
  const offline = useIsOffline();
  const location = useLocation();
  const { authorize: cpAuthorized, userInfo: cpUserInfo } = useSelector((state) => state.cpUserData);
  const { userInfo = null, keycloakLoading } = useKeycloak();
  const sellerCenterAuthorized = useSelector((state) => state?.userData?.authorized);
  const dispatch = useDispatch();
  const scrollRef = useRef(null);
  const hasTimedOutRef = useRef(false);

  const shouldSendCurrent = () => {
    if (isCP()) {
      return cpAuthorized && !!cpUserInfo?.id;
    }
    return !!userInfo?.storeId && sellerCenterAuthorized;
  };

  const shouldShowHeader = ![
    DUBIZZLE_LOGIN_PATH,
    LOGIN_PATH,
    SIGNUP_PATH,
    STORE_NOT_FOUND,
    RESET_PASSWORD_PATH,
    TERMS_AND_CONDITIONS,
  ].includes(sanitizePathname(location.pathname));

  const [termsAndConditionModal, setTermsAndConditionModal] = useState({
    isVisible: false,
    data: {},
  });
  const [isLoading, setIsLoading] = useState(false);
  const [disableVacationModal, setDisableVacationModal] = useState(false);
  const [logoutLoading, setLogoutLoading] = useState(false);

  const shouldShowLayout = ![
    DUBIZZLE_LOGIN_PATH,
    LOGIN_PATH,
    SIGNUP_PATH,
    OMS_NOT_FOUND,
    OMS_SERVER_ERROR,
    CP_NOT_FOUND,
    CP_SERVER_ERROR,
    STORE_NOT_FOUND,
    RESET_PASSWORD_PATH,
    TERMS_AND_CONDITIONS,
  ].includes(sanitizePathname(location.pathname));

  const fetchCurrentUser = async () => {
    try {
      const res = await getCurrentUserApi();
      if (res?.success) {
        const { name, email, profile_image, permission_map, roles, id } = res?.admin || {};
        const image = profile_image?.sizes?.thumbnail;
        dispatch(setUserInfo({ name, email, image, permission_map, roles, id, authorize: true }));
      } else {
        dispatch(clearUserInfo());
      }
    } catch (_) {
      // if (location.pathname !== LOGIN_PATH) {
      //   Toast.error(getErrorString(error));
      // }
      dispatch(clearUserInfo());
    }
  };
  const { loading: cpLoading } = useGetRequest({
    key: ['currentUser'],
    fetch: fetchCurrentUser,
    enabled: cpAuthorized,
  });

  const { loading: tenantLoading, data } = useGetRequest({
    key: ['tenantConfiguration'],
    fetch: () => dispatch(fetchTenantConfiguration('')),
    enabled: shouldSendCurrent(),
  });
  const termsAndConditions =
    typeof data?.payload?.tenant?.terms_and_conditions === 'string'
      ? data?.payload?.tenant?.terms_and_conditions?.replace(
          '[current.date]',
          dayjs().format(DATE_FORMATS.dateMonthYear)
        )
      : data?.payload?.tenant?.terms_and_conditions;

  useEffect(() => {
    let link = document.querySelector('link[rel="shortcut icon"]');
    if (!link) {
      link = document.createElement('link');
      link.id = 'favicon';
      link.rel = 'shortcut icon';
      document.head.appendChild(link);
    }
    link.href = ConfigDB.data.favicon;
    document.title = ConfigDB.data.title;
    scrollRef.current?.scroll({ top: 0 });
  }, [location.pathname]);

  // after 2 hours of inactivity, reload the tab
  useIdleTimer({
    timeout: 2 * 60 * 60 * 1000, // 2 hours
    onIdle: () => {
      hasTimedOutRef.current = true;
    },
    onActive: () => {
      if (hasTimedOutRef.current) {
        hasTimedOutRef.current = false;
        window.location.reload();
      }
    },
  });

  useEffect(() => {
    if (userInfo?.userProfile?.store?.terms_and_conditions_accepted === false && !userInfo?.admin?.id) {
      const isStoreOwner = userInfo?.userProfile?.is_store_owner;
      setTermsAndConditionModal({
        isVisible: true,
        data: {
          title: isStoreOwner ? 'Terms and Conditions' : 'Owner Approval Required',
          description: !isStoreOwner
            ? 'The store owner must first accept the Terms and Conditions before ' +
              'any actions can be performed. Please inform the owner to review and accept the Terms and Conditions to proceed.'
            : null,
          isStoreOwner,
        },
      });
    } else {
      setTermsAndConditionModal({ isVisible: false, data: {} });
    }
  }, [userInfo?.userProfile?.store?.terms_and_conditions_accepted, userInfo?.admin?.id]);

  const onAcceptTermsAndConditions = async () => {
    setIsLoading(true);
    try {
      const res = await acceptTermsAndConditionsApi(userInfo?.storeId);
      if (res?.success) {
        let updatedUserInfo = { ...userInfo };
        updatedUserInfo = {
          ...updatedUserInfo,
          userProfile: {
            ...updatedUserInfo?.userProfile,
            store: {
              ...updatedUserInfo?.userProfile.store,
              terms_and_conditions_accepted: true,
            },
          },
        };
        dispatch(setSellerCenterUserInfo(updatedUserInfo));
        setTermsAndConditionModal({ isVisible: false, data: {} });
      }
    } catch (e) {
      Toast.error(getErrorString(e));
    }
    setIsLoading(false);
  };

  if (cpLoading || keycloakLoading || tenantLoading) {
    return <AppLoader />;
  }

  const onVacation = userInfo?.userProfile?.store?.on_vacation;
  const lastVacationDate = userInfo?.userProfile?.store?.vacation_end_date;
  const store = userInfo?.userProfile?.store;

  return (
    <div>
      <NtnRequirementModal />
      <Modal
        open={
          store?.status?.slug === 'pending' &&
          store?.info_completed &&
          sanitizePathname(location.pathname) !== TERMS_AND_CONDITIONS
        }
        title={strings.account_under_review}
        okText={strings.logout}
        width={'672px'}
        zIndex={9999}
        maskClosable={false}
        closeByKeyboard={false}
        closable={false}
        showActions={true}
        cancelButtonProps={{ style: { display: 'none' } }}
        okButtonProps={{ loading: logoutLoading }}
        handleOk={async () => {
          setLogoutLoading(true);
          await handleOMSLogout();
          setLogoutLoading(false);
        }}
        ModalBody={strings?.your_account_under_review}
      />
      <Modal
        open={!!termsAndConditionModal?.isVisible && !userInfo?.admin?.id}
        title={termsAndConditionModal?.data?.title}
        okText={strings.confirm}
        width={'672px'}
        bodyStyle={{
          height: termsAndConditionModal?.data?.isStoreOwner ? 500 : 'auto',
        }}
        zIndex={9999}
        handleCancel={() => {
          setTermsAndConditionModal({ isVisible: false, data: {} });
        }}
        maskClosable={false}
        closable={false}
        showActions={false}
        closeByKeyboard={false}
        showCustomFooter={termsAndConditionModal?.data?.isStoreOwner}
        customFooter={[
          <Button key="confirm" type="primary" onClick={onAcceptTermsAndConditions} loading={isLoading}>
            Accept and Continue
          </Button>,
        ]}
        ModalBody={
          !termsAndConditionModal?.data?.isStoreOwner ? (
            <p style={{ marginBlock: 0, color: 'var(--gray-500)' }}>{termsAndConditionModal?.data?.description}</p>
          ) : (
            <Flex vertical gap={20}>
              {termsAndConditions ? <div dangerouslySetInnerHTML={{ __html: termsAndConditions }} /> : null}
            </Flex>
          )
        }
      />
      <Layout theme="light">
        {!!offline && <NoInternetBanner />}
        {shouldShowHeader && <TopHeader />}
        <Layout>
          {shouldShowLayout && <SideMenu />}
          <Layout
            {...(shouldShowHeader && {
              style: {
                marginInlineStart: shouldShowLayout ? 72 : 0,
                marginBlockStart: 'var(--ant-layout-header-height)',
                overflowX: 'hidden',
              },
            })}
          >
            <Content
              {...(shouldShowLayout && {
                className: 'main-content',
                style: { backgroundColor: '#F5F5F5' },
              })}
              ref={scrollRef}
            >
              {shouldShowLayout && shouldShowHeader && <TaxValidationBanner />}
              {shouldShowLayout && shouldShowHeader && <AccountStatusBanner />}
              {shouldShowHeader &&
                shouldShowLayout &&
                onVacation &&
                sanitizePathname(location?.pathname) !== PROFILE_SETTINGS && (
                  <Alert
                    icon={<IconVacation />}
                    showIcon
                    type="info"
                    message={
                      <span style={{ fontSize: 'var(--ant-font-size-heading-5)', fontWeight: 600 }}>
                        {strings?.you_are_on_vacation_mode}
                      </span>
                    }
                    description={`${strings?.store_and_listings_hidden_on_olx} ${lastVacationDate ? strings?.until : ''} ${lastVacationDate ? dayjs(lastVacationDate)?.format(DATE_FORMATS.dayMonthYear) : ''}`}
                    action={
                      PermissionsHelper.getPermissions(PERMISSION_KEY_STORE, ACTION_UPDATE) && (
                        <ButtonSecondary
                          onClick={() => {
                            setDisableVacationModal(true);
                          }}
                          type={'link'}
                        >
                          {strings?.disable_vacation_mode}
                        </ButtonSecondary>
                      )
                    }
                    style={{
                      '--ant-margin-xs': 0,
                      gap: 16,
                      alignItems: 'center',
                      '--ant-color-info-bg': '#E9FCFB',
                      '--ant-color-info-border': '#91F2ED',
                    }}
                  />
                )}
              <DisableVacationModal open={disableVacationModal} setOpen={setDisableVacationModal} />
              <Suspense fallback={/*!location.pathname.includes(CHAT_PATH) &&*/ <AppLoader />}>
                <AppRoutes session={userInfo} />
              </Suspense>
            </Content>
          </Layout>
        </Layout>
      </Layout>
    </div>
  );
};

export default App;
